/**
 * Page Editor Component
 * Visual page builder with @dnd-kit drag-and-drop, rich text editing,
 * visual style editor, and inline editing
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import {
  Plus,
  Save,
  Eye,
  Undo2,
  Redo2,
  Smartphone,
  Tablet,
  Monitor,
  X,
  Paintbrush,
  FileText,
  PanelLeftOpen,
  PanelLeftClose,
  Sparkles,
} from 'lucide-react';
import type {
  Page,
  ContentBlock,
  BlockContent,
  BlockStyles,
} from '../../types';
import { RichTextEditor } from '../RichTextEditor';
import { AIDesignAssistant } from '../AIDesignAssistant';
import TrivenyHero from '../../../../admin/pages/TrivenyHero';
import { normalizeAIBlocks } from '../../utils/normalizeAIBlocks';
import { SortableBlock } from './SortableBlock';
import { BlockPalettePanel, type BlockDefinition } from './BlockPalettePanel';
import { DragOverlayBlock } from './DragOverlayBlock';
import { StyleEditor } from './StyleEditor';
import { webPresenceApi } from '../../../../shared/services/platformApi';

// ============================================================================
// TYPES
// ============================================================================

interface PageEditorProps {
  page: Page;
  onSave: (page: Page) => Promise<void>;
  onPublish: () => Promise<void>;
  onPreview: () => void;
  onClose?: () => void;
}

type ViewportMode = 'desktop' | 'tablet' | 'mobile';
type RightPanel = 'content' | 'style' | null;

interface EditorState {
  blocks: ContentBlock[];
  selectedBlockId: string | null;
  hoveredBlockId: string | null;
  history: ContentBlock[][];
  historyIndex: number;
  clipboard: ContentBlock | null;
}

// ============================================================================
// BLOCK DEFINITIONS
// ============================================================================

const BLOCK_DEFINITIONS: BlockDefinition[] = [
  {
    type: 'hero',
    label: 'Hero Section',
    icon: '🎯',
    category: 'Layout',
    defaultContent: {
      headline: 'Welcome to Our Site',
      subheadline: 'Start building something amazing',
      ctaButtons: [{ id: '1', text: 'Get Started', url: '#', style: 'primary' }],
    },
    defaultStyles: { backgroundColor: '#f8fafc', padding: '80px 20px', textAlign: 'center' },
  },
  {
    type: 'richtext',
    label: 'Rich Text',
    icon: '📝',
    category: 'Content',
    defaultContent: { richText: '<p>Start writing your content here...</p>' },
    defaultStyles: { padding: '20px' },
  },
  {
    type: 'text',
    label: 'Text Block',
    icon: '📄',
    category: 'Content',
    defaultContent: { text: 'Add your text content here...' },
    defaultStyles: { padding: '20px' },
  },
  {
    type: 'image',
    label: 'Image',
    icon: '🖼️',
    category: 'Media',
    defaultContent: { imageUrl: '', imageAlt: 'Image description' },
    defaultStyles: { padding: '20px', textAlign: 'center' },
  },
  {
    type: 'video',
    label: 'Video',
    icon: '🎬',
    category: 'Media',
    defaultContent: { videoUrl: '', videoType: 'youtube' },
    defaultStyles: { padding: '20px' },
  },
  {
    type: 'cta',
    label: 'Call to Action',
    icon: '📢',
    category: 'Conversion',
    defaultContent: {
      title: 'Ready to get started?',
      description: 'Join thousands of satisfied customers',
      buttons: [{ id: '1', text: 'Sign Up Now', url: '#', style: 'primary' }],
    },
    defaultStyles: { backgroundColor: '#3b82f6', textColor: '#ffffff', padding: '60px 20px', textAlign: 'center' },
  },
  {
    type: 'features',
    label: 'Features',
    icon: '✨',
    category: 'Content',
    defaultContent: {
      features: [
        { id: '1', title: 'Feature 1', description: 'Description here', icon: '⚡' },
        { id: '2', title: 'Feature 2', description: 'Description here', icon: '🎨' },
        { id: '3', title: 'Feature 3', description: 'Description here', icon: '🚀' },
      ],
    },
    defaultStyles: { padding: '60px 20px' },
  },
  {
    type: 'testimonials',
    label: 'Testimonials',
    icon: '💬',
    category: 'Social Proof',
    defaultContent: {
      testimonials: [{ id: '1', quote: 'This product changed our business!', author: 'John Doe', role: 'CEO', company: 'Acme Inc', rating: 5 }],
    },
    defaultStyles: { backgroundColor: '#f8fafc', padding: '60px 20px' },
  },
  {
    type: 'pricing',
    label: 'Pricing Table',
    icon: '💰',
    category: 'Conversion',
    defaultContent: {
      plans: [
        { id: '1', name: 'Basic', price: '$9', period: '/month', features: ['Feature 1', 'Feature 2'], ctaText: 'Get Started', ctaUrl: '#' },
        { id: '2', name: 'Pro', price: '$29', period: '/month', features: ['Feature 1', 'Feature 2', 'Feature 3'], ctaText: 'Get Started', ctaUrl: '#', isPopular: true },
      ],
    },
    defaultStyles: { padding: '60px 20px' },
  },
  {
    type: 'faq',
    label: 'FAQ',
    icon: '❓',
    category: 'Content',
    defaultContent: {
      faqs: [
        { id: '1', question: 'Question 1?', answer: 'Answer 1' },
        { id: '2', question: 'Question 2?', answer: 'Answer 2' },
      ],
    },
    defaultStyles: { padding: '60px 20px' },
  },
  { type: 'form', label: 'Form', icon: '📋', category: 'Conversion', defaultContent: { formId: '' }, defaultStyles: { padding: '40px 20px' } },
  { type: 'divider', label: 'Divider', icon: '➖', category: 'Layout', defaultContent: {}, defaultStyles: { padding: '20px' } },
  { type: 'spacer', label: 'Spacer', icon: '↕️', category: 'Layout', defaultContent: {}, defaultStyles: { padding: '40px 0' } },
  { type: 'columns', label: 'Columns', icon: '▤', category: 'Layout', defaultContent: { columns: 2, children: [] }, defaultStyles: { padding: '20px' } },
  { type: 'html', label: 'Custom HTML', icon: '🔧', category: 'Advanced', defaultContent: { html: '<div>Custom HTML here</div>' }, defaultStyles: { padding: '20px' } },
  { type: 'custom_code', label: 'Custom Code', icon: '💻', category: 'Advanced', defaultContent: { customHtml: '', customCss: '', customJs: '' }, defaultStyles: { padding: '20px' } },
];

const BLOCK_CATEGORIES = ['Layout', 'Content', 'Media', 'Conversion', 'Social Proof', 'Advanced'];

// ============================================================================
// HELPERS
// ============================================================================

function generateBlockId(): string {
  return `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function createBlock(definition: BlockDefinition, sortOrder: number): ContentBlock {
  return {
    id: generateBlockId(),
    pageId: '',
    parentBlockId: null,
    blockType: definition.type,
    blockName: definition.label,
    content: { ...definition.defaultContent },
    styles: { ...definition.defaultStyles },
    settings: null,
    sortOrder,
    isVisible: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function blockStylesToCSS(styles: BlockStyles | null): React.CSSProperties {
  if (!styles) return {};
  const s = styles;
  return {
    backgroundColor: s.backgroundColor,
    backgroundImage: s.backgroundGradient || (s.backgroundImage ? `url(${s.backgroundImage})` : undefined),
    backgroundSize: s.backgroundSize,
    backgroundPosition: s.backgroundPosition,
    color: s.color || s.textColor,
    display: s.display as any,
    flexDirection: s.flexDirection as any,
    justifyContent: s.justifyContent,
    alignItems: s.alignItems,
    gap: s.gap,
    gridTemplateColumns: s.gridTemplateColumns,
    padding: s.padding,
    paddingTop: s.paddingTop,
    paddingRight: s.paddingRight,
    paddingBottom: s.paddingBottom,
    paddingLeft: s.paddingLeft,
    margin: s.margin,
    marginTop: s.marginTop,
    marginRight: s.marginRight,
    marginBottom: s.marginBottom,
    marginLeft: s.marginLeft,
    width: s.width,
    height: s.height,
    minWidth: s.minWidth,
    maxWidth: s.maxWidth,
    minHeight: s.minHeight,
    maxHeight: s.maxHeight,
    overflow: s.overflow as any,
    fontFamily: s.fontFamily,
    fontSize: s.fontSize,
    fontWeight: s.fontWeight as any,
    lineHeight: s.lineHeight,
    letterSpacing: s.letterSpacing,
    textAlign: s.textAlign as any,
    textDecoration: s.textDecoration,
    borderWidth: s.borderWidth,
    borderStyle: s.borderStyle as any,
    borderColor: s.borderColor,
    borderRadius: s.borderRadius,
    borderTopLeftRadius: s.borderTopLeftRadius,
    borderTopRightRadius: s.borderTopRightRadius,
    borderBottomLeftRadius: s.borderBottomLeftRadius,
    borderBottomRightRadius: s.borderBottomRightRadius,
    boxShadow: s.boxShadow,
    opacity: s.opacity ? parseFloat(s.opacity) : undefined,
    transform: s.transform,
    transition: s.transition,
  };
}

// ============================================================================
// BLOCK RENDERER
// ============================================================================

interface BlockRendererProps {
  block: ContentBlock;
  onUpdateContent: (content: Partial<BlockContent>) => void;
  isSelected: boolean;
}

function BlockRenderer({ block, onUpdateContent, isSelected }: BlockRendererProps) {
  const style = blockStylesToCSS(block.styles);

  switch (block.blockType) {
    case 'hero':
      return (
        <div style={style} className="min-h-[300px] flex flex-col items-center justify-center">
          <h1
            className="text-4xl font-bold mb-4 outline-none"
            contentEditable={isSelected}
            suppressContentEditableWarning
            onBlur={(e) => onUpdateContent({ headline: e.currentTarget.textContent || '' })}
          >
            {block.content.headline}
          </h1>
          <p
            className="text-xl opacity-80 mb-8 outline-none"
            contentEditable={isSelected}
            suppressContentEditableWarning
            onBlur={(e) => onUpdateContent({ subheadline: e.currentTarget.textContent || '' })}
          >
            {block.content.subheadline}
          </p>
          <div className="flex gap-4">
            {block.content.ctaButtons?.map((btn) => (
              <button
                key={btn.id}
                className={`px-6 py-3 rounded-lg font-medium ${
                  btn.style === 'primary' ? 'bg-blue-600 text-white' : 'border border-gray-300'
                }`}
              >
                {btn.text}
              </button>
            ))}
          </div>
        </div>
      );

    case 'richtext':
      return (
        <div style={style}>
          <RichTextEditor
            content={block.content.richText || '<p></p>'}
            onChange={(html) => onUpdateContent({ richText: html })}
            editable={true}
            showToolbar={isSelected}
            minHeight="100px"
          />
        </div>
      );

    case 'text':
      return (
        <div style={style} className="prose max-w-none">
          {isSelected ? (
            <RichTextEditor
              content={block.content.richText || `<p>${block.content.text || ''}</p>`}
              onChange={(html) => onUpdateContent({ richText: html, text: html.replace(/<[^>]+>/g, '') })}
              showToolbar={true}
              minHeight="60px"
            />
          ) : block.content.richText ? (
            <div dangerouslySetInnerHTML={{ __html: block.content.richText }} />
          ) : (
            <p>{block.content.text}</p>
          )}
        </div>
      );

    case 'image':
      return (
        <div style={style}>
          {block.content.imageUrl ? (
            <img src={block.content.imageUrl} alt={block.content.imageAlt} className="max-w-full h-auto" />
          ) : (
            <div
              className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center cursor-pointer hover:bg-gray-200 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                const url = window.prompt('Enter image URL:');
                if (url) {
                  onUpdateContent({ imageUrl: url });
                }
              }}
            >
              <span className="text-4xl block mb-2">🖼️</span>
              <span className="text-gray-500">Click to add image URL</span>
            </div>
          )}
          {block.content.caption && <p className="text-sm text-gray-600 mt-2">{block.content.caption}</p>}
        </div>
      );

    case 'video': {
      const videoUrl = block.content.videoUrl || '';
      const videoType = block.content.videoType || 'youtube';
      let embedUrl = '';
      if (videoUrl) {
        if (videoType === 'youtube') {
          // Handle youtube.com/watch?v=ID, youtu.be/ID, and youtube.com/embed/ID
          const ytMatch = videoUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
          if (ytMatch) embedUrl = `https://www.youtube.com/embed/${ytMatch[1]}`;
        } else if (videoType === 'vimeo') {
          const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
          if (vimeoMatch) embedUrl = `https://player.vimeo.com/video/${vimeoMatch[1]}`;
        } else if (videoType === 'url') {
          embedUrl = videoUrl;
        }
      }
      return (
        <div style={style}>
          {embedUrl ? (
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={embedUrl}
                className="absolute inset-0 w-full h-full rounded-lg"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Video embed"
              />
            </div>
          ) : (
            <div
              className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center cursor-pointer hover:bg-gray-200 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                const url = window.prompt('Enter video URL (YouTube or Vimeo):');
                if (url) {
                  const type = url.includes('vimeo.com') ? 'vimeo' : 'youtube';
                  onUpdateContent({ videoUrl: url, videoType: type });
                }
              }}
            >
              <span className="text-4xl block mb-2">🎬</span>
              <span className="text-gray-500">Click to add video URL</span>
            </div>
          )}
          {block.content.caption && <p className="text-sm text-gray-600 mt-2">{block.content.caption}</p>}
        </div>
      );
    }

    case 'cta':
      return (
        <div style={style}>
          <h2
            className="text-3xl font-bold mb-4 outline-none"
            contentEditable={isSelected}
            suppressContentEditableWarning
            onBlur={(e) => onUpdateContent({ title: e.currentTarget.textContent || '' })}
          >
            {block.content.title}
          </h2>
          <p
            className="text-lg mb-6 outline-none"
            contentEditable={isSelected}
            suppressContentEditableWarning
            onBlur={(e) => onUpdateContent({ description: e.currentTarget.textContent || '' })}
          >
            {block.content.description}
          </p>
          <div className="flex justify-center gap-4">
            {block.content.buttons?.map((btn) => (
              <button
                key={btn.id}
                className={`px-6 py-3 rounded-lg font-medium ${
                  btn.style === 'primary' ? 'bg-white text-blue-600' : 'border border-white text-white'
                }`}
              >
                {btn.text}
              </button>
            ))}
          </div>
        </div>
      );

    case 'features':
      return (
        <div style={style}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {block.content.features?.map((feature) => (
              <div key={feature.id} className="text-center">
                <span className="text-4xl mb-4 block">{feature.icon}</span>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      );

    case 'testimonials':
      return (
        <div style={style}>
          <div className="max-w-2xl mx-auto">
            {block.content.testimonials?.map((testimonial) => (
              <div key={testimonial.id} className="text-center">
                <blockquote className="text-xl italic mb-4">&ldquo;{testimonial.quote}&rdquo;</blockquote>
                <p className="font-semibold">{testimonial.author}</p>
                <p className="text-gray-600">{testimonial.role}, {testimonial.company}</p>
              </div>
            ))}
          </div>
        </div>
      );

    case 'divider':
      return <div style={style}><hr className="border-gray-200" /></div>;

    case 'spacer':
      return <div style={style} />;

    case 'html':
      return <div style={style} dangerouslySetInnerHTML={{ __html: block.content.html || '' }} />;

    case 'custom_code':
      return (
        <div style={style} className="bg-gray-900 text-gray-100 rounded-lg p-4">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
            <span className="bg-gray-700 px-2 py-0.5 rounded">Custom Code Block</span>
          </div>
          {block.content.customHtml ? (
            <pre className="text-xs font-mono overflow-auto max-h-40">{block.content.customHtml.substring(0, 200)}{(block.content.customHtml.length || 0) > 200 ? '...' : ''}</pre>
          ) : (
            <p className="text-sm text-gray-500">Click to add custom HTML, CSS, and JavaScript</p>
          )}
        </div>
      );

    case 'triveny_hero':
      // The canonical Triveny hero, rendered inline (contained, static frame).
      // Same component + CSS as the public site, so the canvas matches what
      // ships. Edit the statements in the inspector; full-screen via Preview.
      return (
        <div style={{ ...style, height: 600, overflow: 'hidden', borderRadius: 8, border: '1px solid #e6e8ec' }}>
          <TrivenyHero
            contained
            cycleMs={0}
            loginUrl="#"
            loginLabel="Log in"
            statements={(block.content as any)?.statements}
          />
        </div>
      );

    default:
      return (
        <div style={style} className="p-8 bg-gray-50 border rounded text-center">
          <span className="text-gray-500">{block.blockType} block preview</span>
        </div>
      );
  }
}

// ============================================================================
// CONTENT SETTINGS PANEL
// ============================================================================

function ContentSettingsPanel({ block, onUpdate }: { block: ContentBlock; onUpdate: (updates: Partial<ContentBlock>) => void }) {
  const content = block.content;
  const updateContent = (updates: Partial<BlockContent>) => {
    onUpdate({ content: { ...content, ...updates } });
  };

  return (
    <div className="space-y-4">
      {block.blockType === 'triveny_hero' && (
        <>
          <p className="text-xs text-gray-500">
            The Triveny hero — Design / Market / Engage statements that cycle on the
            public site. Layout and styling are fixed (pixel-matched to the built-in
            hero); edit the three statements below.
          </p>
          {(['Design', 'Market', 'Engage'] as const).map((pillar) => {
            const statements = ((content as any).statements || {}) as Record<string, string>;
            const defaults: Record<string, string> = {
              Design: 'Prompt it. Create it.',
              Market: 'All your GTM tools in one place.',
              Engage: 'Move every deal forward.',
            };
            return (
              <div key={pillar}>
                <label className="block text-xs text-gray-500 mb-1">{pillar} statement</label>
                <input
                  type="text"
                  value={statements[pillar] ?? ''}
                  placeholder={defaults[pillar]}
                  onChange={(e) => updateContent({ statements: { ...statements, [pillar]: e.target.value } } as any)}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
            );
          })}
        </>
      )}

      {block.blockType === 'hero' && (
        <>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Headline</label>
            <input type="text" value={content.headline || ''} onChange={(e) => updateContent({ headline: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Subheadline</label>
            <input type="text" value={content.subheadline || ''} onChange={(e) => updateContent({ subheadline: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Background Image URL</label>
            <input type="text" value={content.backgroundImage || ''} onChange={(e) => updateContent({ backgroundImage: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" placeholder="https://..." />
          </div>
        </>
      )}

      {(block.blockType === 'text' || block.blockType === 'richtext') && (
        <div>
          <label className="block text-xs text-gray-500 mb-1">Content</label>
          <RichTextEditor
            content={content.richText || `<p>${content.text || ''}</p>`}
            onChange={(html) => updateContent({ richText: html, text: html.replace(/<[^>]+>/g, '') })}
            minHeight="200px"
          />
        </div>
      )}

      {block.blockType === 'image' && (
        <>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Image URL</label>
            <input type="text" value={content.imageUrl || ''} onChange={(e) => updateContent({ imageUrl: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Alt Text</label>
            <input type="text" value={content.imageAlt || ''} onChange={(e) => updateContent({ imageAlt: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Caption</label>
            <input type="text" value={content.caption || ''} onChange={(e) => updateContent({ caption: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
          </div>
        </>
      )}

      {block.blockType === 'video' && (
        <>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Video URL</label>
            <input
              type="text"
              value={content.videoUrl || ''}
              onChange={(e) => {
                const url = e.target.value;
                const type = url.includes('vimeo.com') ? 'vimeo' : 'youtube';
                updateContent({ videoUrl: url, videoType: type });
              }}
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="https://www.youtube.com/watch?v=..."
            />
            <p className="text-xs text-gray-400 mt-1">Paste a YouTube or Vimeo URL</p>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Video Source</label>
            <select
              value={content.videoType || 'youtube'}
              onChange={(e) => updateContent({ videoType: e.target.value })}
              className="w-full border rounded px-3 py-2 text-sm"
            >
              <option value="youtube">YouTube</option>
              <option value="vimeo">Vimeo</option>
              <option value="url">Direct URL</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Caption</label>
            <input type="text" value={content.caption || ''} onChange={(e) => updateContent({ caption: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
          </div>
        </>
      )}

      {block.blockType === 'cta' && (
        <>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Title</label>
            <input type="text" value={content.title || ''} onChange={(e) => updateContent({ title: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Description</label>
            <textarea value={content.description || ''} onChange={(e) => updateContent({ description: e.target.value })} className="w-full border rounded px-3 py-2 text-sm h-20" />
          </div>
        </>
      )}

      {block.blockType === 'html' && (
        <div>
          <label className="block text-xs text-gray-500 mb-1">HTML Code</label>
          <textarea value={content.html || ''} onChange={(e) => updateContent({ html: e.target.value })} className="w-full border rounded px-3 py-2 text-xs font-mono h-48" spellCheck={false} />
        </div>
      )}

      {block.blockType === 'custom_code' && (
        <>
          <div>
            <label className="block text-xs text-gray-500 mb-1">HTML</label>
            <textarea value={content.customHtml || ''} onChange={(e) => updateContent({ customHtml: e.target.value })} className="w-full border rounded px-3 py-2 text-xs font-mono h-32" spellCheck={false} placeholder="<div>...</div>" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">CSS</label>
            <textarea value={content.customCss || ''} onChange={(e) => updateContent({ customCss: e.target.value })} className="w-full border rounded px-3 py-2 text-xs font-mono h-24" spellCheck={false} placeholder=".my-class { ... }" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">JavaScript</label>
            <textarea value={content.customJs || ''} onChange={(e) => updateContent({ customJs: e.target.value })} className="w-full border rounded px-3 py-2 text-xs font-mono h-24" spellCheck={false} placeholder="console.log('Hello');" />
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function PageEditor({ page, onSave, onPublish, onPreview, onClose }: PageEditorProps) {
  const [state, setState] = useState<EditorState>({
    blocks: page.contentBlocks || [],
    selectedBlockId: null,
    hoveredBlockId: null,
    history: [page.contentBlocks || []],
    historyIndex: 0,
    clipboard: null,
  });

  const [viewport, setViewport] = useState<ViewportMode>('desktop');
  const [rightPanel, setRightPanel] = useState<RightPanel>(null);
  const [showPalette, setShowPalette] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeDragData, setActiveDragData] = useState<BlockDefinition | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [showAIPanel, setShowAIPanel] = useState(false);
  // Floating Inspector drag position (null = default top-right anchor).
  const [inspectorPos, setInspectorPos] = useState<{ x: number; y: number } | null>(null);
  // Per-block "Edit with AI" prompt modal.
  const [aiEditBlockId, setAiEditBlockId] = useState<string | null>(null);
  const [aiEditPrompt, setAiEditPrompt] = useState('');
  const [aiEditLoading, setAiEditLoading] = useState(false);
  const [aiEditError, setAiEditError] = useState<string | null>(null);

  // Drag the floating Inspector by its title bar.
  const startInspectorDrag = (e: React.PointerEvent) => {
    const panel = (e.currentTarget as HTMLElement).closest('[data-inspector]') as HTMLElement | null;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const offX = e.clientX - rect.left;
    const offY = e.clientY - rect.top;
    const move = (ev: PointerEvent) => setInspectorPos({ x: ev.clientX - offX, y: ev.clientY - offY });
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };

  const handleClose = onClose || onPreview;

  const editorRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  // History
  const pushHistory = useCallback((blocks: ContentBlock[]) => {
    setState((prev) => ({
      ...prev,
      blocks,
      history: [...prev.history.slice(0, prev.historyIndex + 1), blocks],
      historyIndex: prev.historyIndex + 1,
    }));
  }, []);

  const undo = useCallback(() => {
    setState((prev) => prev.historyIndex <= 0 ? prev : {
      ...prev,
      blocks: prev.history[prev.historyIndex - 1],
      historyIndex: prev.historyIndex - 1,
    });
  }, []);

  const redo = useCallback(() => {
    setState((prev) => prev.historyIndex >= prev.history.length - 1 ? prev : {
      ...prev,
      blocks: prev.history[prev.historyIndex + 1],
      historyIndex: prev.historyIndex + 1,
    });
  }, []);

  // Block operations
  const addBlock = useCallback((definition: BlockDefinition) => {
    const newBlock = createBlock(definition, state.blocks.length);
    newBlock.pageId = page.id;
    pushHistory([...state.blocks, newBlock]);
    setState((prev) => ({ ...prev, selectedBlockId: newBlock.id }));
    setRightPanel('content');
  }, [state.blocks, page.id, pushHistory]);

  const updateBlock = useCallback((blockId: string, updates: Partial<ContentBlock>) => {
    const newBlocks = state.blocks.map((b) =>
      b.id === blockId ? { ...b, ...updates, updatedAt: new Date().toISOString() } : b
    );
    pushHistory(newBlocks);
  }, [state.blocks, pushHistory]);

  // "Edit with AI": send the block + instruction to the backend, apply the
  // edited block back (content/styles/name). Brand-constrained server-side.
  const runAiEdit = useCallback(async () => {
    if (!aiEditBlockId || !aiEditPrompt.trim()) return;
    const block = state.blocks.find((b) => b.id === aiEditBlockId);
    if (!block) return;
    setAiEditLoading(true);
    setAiEditError(null);
    try {
      const res: any = await webPresenceApi.aiEditBlock({
        block: block as any,
        instruction: aiEditPrompt.trim(),
        site_id: page.siteId,
        page_id: page.id,
      });
      const edited = res?.data?.block || res?.block;
      if (!edited) {
        setAiEditError('No changes returned. Try rephrasing.');
        return;
      }
      updateBlock(aiEditBlockId, {
        content: { ...block.content, ...(edited.content || {}) },
        styles: edited.styles ?? block.styles,
        blockName: edited.blockName ?? block.blockName,
      });
      setAiEditBlockId(null);
      setAiEditPrompt('');
    } catch (e: any) {
      setAiEditError(e?.message || 'Edit failed');
    } finally {
      setAiEditLoading(false);
    }
  }, [aiEditBlockId, aiEditPrompt, state.blocks, page.siteId, page.id, updateBlock]);

  const deleteBlock = useCallback((blockId: string) => {
    const newBlocks = state.blocks.filter((b) => b.id !== blockId).map((b, i) => ({ ...b, sortOrder: i }));
    pushHistory(newBlocks);
    setState((prev) => ({
      ...prev,
      selectedBlockId: prev.selectedBlockId === blockId ? null : prev.selectedBlockId,
    }));
    if (state.selectedBlockId === blockId) setRightPanel(null);
  }, [state.blocks, state.selectedBlockId, pushHistory]);

  const duplicateBlock = useCallback((blockId: string) => {
    const idx = state.blocks.findIndex((b) => b.id === blockId);
    if (idx === -1) return;
    const newBlock: ContentBlock = {
      ...JSON.parse(JSON.stringify(state.blocks[idx])),
      id: generateBlockId(),
      sortOrder: idx + 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const newBlocks = [
      ...state.blocks.slice(0, idx + 1),
      newBlock,
      ...state.blocks.slice(idx + 1).map((b) => ({ ...b, sortOrder: b.sortOrder + 1 })),
    ];
    pushHistory(newBlocks);
  }, [state.blocks, pushHistory]);

  const moveBlock = useCallback((blockId: string, direction: 'up' | 'down') => {
    const idx = state.blocks.findIndex((b) => b.id === blockId);
    if (idx === -1) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= state.blocks.length) return;
    pushHistory(arrayMove(state.blocks, idx, newIdx).map((b, i) => ({ ...b, sortOrder: i })));
  }, [state.blocks, pushHistory]);

  // Drag handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current;
    if (data?.type === 'palette-block') setActiveDragData(data.definition);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveDragData(null);
    const { active, over } = event;
    const activeData = active.data.current;
    // Always add palette blocks, even if dropped on empty canvas (no over target)
    if (activeData?.type === 'palette-block') {
      addBlock(activeData.definition);
      return;
    }
    if (!over) return;
    if (active.id !== over.id) {
      const oldIndex = state.blocks.findIndex((b) => b.id === active.id);
      const newIndex = state.blocks.findIndex((b) => b.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        pushHistory(arrayMove(state.blocks, oldIndex, newIndex).map((b, i) => ({ ...b, sortOrder: i })));
      }
    }
  }, [state.blocks, addBlock, pushHistory]);

  // Keyboard shortcuts - skip when user is inside an editor or input
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const el = e.target as HTMLElement;
    const isInEditor = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el.isContentEditable || !!el.closest('[contenteditable="true"]') || !!el.closest('.tiptap') || !!el.closest('.ProseMirror');

    if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !isInEditor) {
      e.preventDefault();
      e.shiftKey ? redo() : undo();
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'c' && state.selectedBlockId && !isInEditor) {
      const block = state.blocks.find((b) => b.id === state.selectedBlockId);
      if (block) setState((prev) => ({ ...prev, clipboard: { ...block } }));
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'v' && state.clipboard && !isInEditor) {
      const newBlock: ContentBlock = {
        ...JSON.parse(JSON.stringify(state.clipboard)),
        id: generateBlockId(),
        pageId: page.id,
        sortOrder: state.blocks.length,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      pushHistory([...state.blocks, newBlock]);
    }
    if ((e.key === 'Delete' || e.key === 'Backspace') && state.selectedBlockId && !isInEditor) {
      e.preventDefault();
      deleteBlock(state.selectedBlockId);
    }
  }, [state, undo, redo, deleteBlock, pushHistory, page.id]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await onSave({ ...page, contentBlocks: state.blocks });
    } finally {
      setIsSaving(false);
    }
  }, [page, state.blocks, onSave]);

  const selectedBlock = state.selectedBlockId ? state.blocks.find((b) => b.id === state.selectedBlockId) : null;

  const viewportWidth: Record<ViewportMode, string> = { desktop: '100%', tablet: '768px', mobile: '375px' };

  return (
    <div className="h-screen flex flex-col bg-gray-100" onKeyDown={handleKeyDown} tabIndex={0}>
      {/* Toolbar */}
      <div className="bg-white border-b px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded" title="Close editor">
            <X className="h-4 w-4" />
          </button>
          {!isPreviewMode && (
            <>
              <div className="w-px h-5 bg-gray-200" />
              <button onClick={() => setShowPalette(!showPalette)} className="p-2 hover:bg-gray-100 rounded" title={showPalette ? 'Hide blocks' : 'Show blocks'}>
                {showPalette ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
              </button>
            </>
          )}
          <h1 className="font-semibold text-gray-900">{page.pageTitle}</h1>
          <span className={`text-xs px-2 py-0.5 rounded-full ${page.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
            {page.status === 'published' ? 'Published' : 'Draft'}
          </span>
          {isPreviewMode && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Preview Mode</span>}
        </div>

        <div className="flex items-center gap-2">
          {!isPreviewMode && (
            <>
              <button onClick={undo} disabled={state.historyIndex === 0} className="p-2 hover:bg-gray-100 rounded disabled:opacity-30" title="Undo"><Undo2 className="h-4 w-4" /></button>
              <button onClick={redo} disabled={state.historyIndex === state.history.length - 1} className="p-2 hover:bg-gray-100 rounded disabled:opacity-30" title="Redo"><Redo2 className="h-4 w-4" /></button>
              <div className="w-px h-6 bg-gray-200 mx-1" />
            </>
          )}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            {(['desktop', 'tablet', 'mobile'] as const).map((mode) => {
              const Icon = { desktop: Monitor, tablet: Tablet, mobile: Smartphone }[mode];
              return <button key={mode} onClick={() => setViewport(mode)} className={`p-1.5 rounded ${viewport === mode ? 'bg-white shadow-sm' : ''}`}><Icon className="h-4 w-4" /></button>;
            })}
          </div>
          <div className="w-px h-6 bg-gray-200 mx-1" />
          {!isPreviewMode && (
            <button onClick={() => setShowAIPanel(!showAIPanel)} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded transition-colors ${showAIPanel ? 'bg-violet-100 text-violet-700' : 'hover:bg-gray-100'}`} title="AI Design Assistant"><Sparkles className="h-4 w-4" /> AI</button>
          )}
          <button onClick={() => setIsPreviewMode(!isPreviewMode)} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded ${isPreviewMode ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}><Eye className="h-4 w-4" /> {isPreviewMode ? 'Edit' : 'Preview'}</button>
          {!isPreviewMode && (
            <>
              <button onClick={handleSave} disabled={isSaving} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-900 text-white rounded hover:bg-gray-800 disabled:opacity-50"><Save className="h-4 w-4" /> {isSaving ? 'Saving...' : 'Save'}</button>
              <button onClick={onPublish} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">Publish</button>
            </>
          )}
        </div>
      </div>

      {/* Editor */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex-1 overflow-hidden flex">
          {/* Left: Block Palette */}
          {showPalette && !isPreviewMode && <BlockPalettePanel definitions={BLOCK_DEFINITIONS} categories={BLOCK_CATEGORIES} onAddBlock={addBlock} />}

          {/* Center: Canvas */}
          <div className="flex-1 overflow-auto p-8 flex justify-center">
            <div ref={editorRef} style={{ width: viewportWidth[viewport] }} className="bg-white shadow-lg min-h-full transition-all duration-300 max-w-full">
              {isPreviewMode ? (
                /* Preview mode - render blocks without editing controls */
                <div>
                  {state.blocks.sort((a, b) => a.sortOrder - b.sortOrder).map((block) => (
                    <BlockRenderer
                      key={block.id}
                      block={block}
                      isSelected={false}
                      onUpdateContent={() => {}}
                    />
                  ))}
                  {state.blocks.length === 0 && (
                    <div className="flex items-center justify-center h-96 text-gray-400">
                      <p>No content to preview</p>
                    </div>
                  )}
                </div>
              ) : state.blocks.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                  <p className="text-lg font-medium text-gray-700 mb-2">Start building your page</p>
                  <p className="text-sm text-gray-400 mb-6">Click a block below, drag from the left, or use AI to generate content</p>
                  <button
                    onClick={() => setShowAIPanel(true)}
                    className="flex items-center gap-2 px-4 py-2.5 mb-6 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors shadow-sm"
                  >
                    <Sparkles className="h-4 w-4" /> Generate with AI
                  </button>
                  <div className="grid grid-cols-3 gap-3 max-w-md">
                    {BLOCK_DEFINITIONS.filter(d => ['hero', 'richtext', 'text', 'image', 'cta', 'columns'].includes(d.type)).map(def => (
                      <button key={def.type} onClick={() => addBlock(def)} className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors">
                        <span className="text-2xl">{def.icon}</span>
                        <span className="text-xs font-medium text-gray-600">{def.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <SortableContext items={state.blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                  <div>
                    {state.blocks.sort((a, b) => a.sortOrder - b.sortOrder).map((block) => (
                      <SortableBlock
                        key={block.id}
                        block={block}
                        isSelected={state.selectedBlockId === block.id}
                        isHovered={state.hoveredBlockId === block.id}
                        onSelect={() => { setState((prev) => ({ ...prev, selectedBlockId: block.id })); if (!rightPanel) setRightPanel('content'); }}
                        onHover={(h) => setState((prev) => ({ ...prev, hoveredBlockId: h ? block.id : null }))}
                        onDelete={() => deleteBlock(block.id)}
                        onDuplicate={() => duplicateBlock(block.id)}
                        onMoveUp={() => moveBlock(block.id, 'up')}
                        onMoveDown={() => moveBlock(block.id, 'down')}
                        onEditWithAI={() => { setState((prev) => ({ ...prev, selectedBlockId: block.id })); setAiEditBlockId(block.id); }}
                      >
                        <BlockRenderer
                          block={block}
                          isSelected={state.selectedBlockId === block.id}
                          onUpdateContent={(updates) => updateBlock(block.id, { content: { ...block.content, ...updates } })}
                        />
                      </SortableBlock>
                    ))}
                    <div className="p-8 flex justify-center">
                      <button onClick={() => setShowPalette(true)} className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors">
                        <Plus className="h-4 w-4" /> Add Block
                      </button>
                    </div>
                  </div>
                </SortableContext>
              )}
            </div>
          </div>

          {/* Floating, draggable Inspector (Figma-style) */}
          {selectedBlock && rightPanel && !isPreviewMode && (
            <div
              data-inspector
              className="bg-white border border-gray-200 rounded-xl shadow-2xl flex flex-col z-40"
              style={{
                position: 'fixed',
                top: inspectorPos ? inspectorPos.y : 84,
                left: inspectorPos ? inspectorPos.x : undefined,
                right: inspectorPos ? undefined : 24,
                width: 320,
                maxHeight: 'calc(100vh - 120px)',
              }}
            >
              {/* Draggable title bar */}
              <div
                onPointerDown={startInspectorDrag}
                className="flex items-center gap-2 px-3 py-2 border-b bg-gray-50 rounded-t-xl cursor-move select-none"
              >
                <span className="text-xs font-medium text-gray-700 flex-1 truncate">
                  {selectedBlock.blockName || selectedBlock.blockType}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); setAiEditBlockId(selectedBlock.id); }}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-violet-700 hover:bg-violet-50 rounded"
                  title="Edit this block with AI"
                >
                  <Sparkles className="h-3.5 w-3.5" /> Edit with AI
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setRightPanel(null); setState((prev) => ({ ...prev, selectedBlockId: null })); }}
                  className="px-1 py-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {/* Content / Style tabs */}
              <div className="flex border-b">
                <button onClick={() => setRightPanel('content')} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium border-b-2 ${rightPanel === 'content' ? 'border-blue-500 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  <FileText className="h-3.5 w-3.5" /> Content
                </button>
                <button onClick={() => setRightPanel('style')} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium border-b-2 ${rightPanel === 'style' ? 'border-blue-500 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  <Paintbrush className="h-3.5 w-3.5" /> Style
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {rightPanel === 'content' ? (
                  <div className="p-4"><ContentSettingsPanel block={selectedBlock} onUpdate={(updates) => updateBlock(selectedBlock.id, updates)} /></div>
                ) : (
                  <StyleEditor styles={selectedBlock.styles || {}} onChange={(styles) => updateBlock(selectedBlock.id, { styles })} />
                )}
              </div>
            </div>
          )}

          {/* AI Design Assistant Panel */}
          {showAIPanel && !isPreviewMode && (
            <AIDesignAssistant
              siteId={page.siteId}
              pageId={page.id}
              isOpen={showAIPanel}
              onClose={() => setShowAIPanel(false)}
              onApplyBlocks={(blocks) => {
                const normalized = normalizeAIBlocks(blocks, page.id);
                // Re-index sort orders starting after existing blocks
                normalized.forEach((b, i) => { b.sortOrder = state.blocks.length + i; });
                pushHistory([...state.blocks, ...normalized]);
              }}
              onApplySeo={(seo) => {
                // SEO is saved via the page save — we can't directly update here,
                // but the parent component can handle it through onSave
              }}
            />
          )}
        </div>

        {/* Edit-with-AI prompt modal */}
        {aiEditBlockId && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
            onClick={() => { if (!aiEditLoading) { setAiEditBlockId(null); setAiEditError(null); } }}
          >
            <div className="bg-white rounded-xl shadow-2xl w-[440px] max-w-[92vw] p-5" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-violet-600" />
                <span className="text-sm font-semibold text-gray-800">Edit block with AI</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Describe the change — e.g. &ldquo;make the headline punchier&rdquo;, &ldquo;warmer tone&rdquo;, or
                &ldquo;add a third feature&rdquo;. Edits stay on-brand.
              </p>
              <textarea
                value={aiEditPrompt}
                onChange={(e) => setAiEditPrompt(e.target.value)}
                rows={3}
                autoFocus
                placeholder="What should change?"
                className="w-full border rounded-lg p-2 text-sm"
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) runAiEdit(); }}
              />
              {aiEditError && <p className="text-xs text-red-600 mt-1">{aiEditError}</p>}
              <div className="flex justify-end gap-2 mt-3">
                <button
                  onClick={() => { setAiEditBlockId(null); setAiEditError(null); }}
                  disabled={aiEditLoading}
                  className="px-3 py-1.5 text-sm rounded-lg border hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={runAiEdit}
                  disabled={aiEditLoading || !aiEditPrompt.trim()}
                  className="px-4 py-1.5 text-sm rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 disabled:opacity-50"
                >
                  {aiEditLoading ? 'Editing…' : 'Apply'}
                </button>
              </div>
            </div>
          </div>
        )}

        <DragOverlay>{activeDragData && <DragOverlayBlock definition={activeDragData} />}</DragOverlay>
      </DndContext>
    </div>
  );
}

export default PageEditor;
